import { useEffect, useRef, useState } from 'react';
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik';

import { InputText } from 'components/forms/InputText';
import { GenericLoading } from 'components/ui/Loader';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';
import { usePanel } from 'hooks/usePanel';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';

import {
  addCollaborator,
  createAndAddCollaborator,
} from '../../../api/personneMoraleAccessApi';
import {
  AddCollaboratorFieldNames,
  AddCollaboratorFormValues,
  addCollaboratorFormConfig,
} from './addCollaboratorFormConfig';

interface AddCollaboratorFormProps {
  personneMoraleId: string;
  onSuccess: () => void;
}

/** Remonte la validité Formik au parent APRÈS le rendu (un setState pendant le
 * render de Formik déclenche l'avertissement React « cannot update while
 * rendering a different component »). */
const ValiditySync: React.FC<{ onChange: (valid: boolean) => void }> = ({ onChange }) => {
  const { isValid } = useFormikContext();
  useEffect(() => {
    onChange(isValid);
  }, [isValid, onChange]);
  return null;
};

/**
 * Formulaire d'ajout d'un collaborateur (workflow unifié).
 *
 * Comportement :
 *  - Tente d'abord `addCollaborator` (user existant)
 *  - Si l'email n'existe pas et que nom+prénom sont renseignés → tente `createAndAddCollaborator`
 *  - Sinon → message d'erreur "saisir nom et prénom pour créer le compte"
 *
 * Affiche le mot de passe temporaire dans une ConfirmationOverlay non-destructive
 * après création d'un nouveau compte, pour que l'Owner puisse le copier.
 */
export const AddCollaboratorForm: React.FC<AddCollaboratorFormProps> = ({
  personneMoraleId,
  onSuccess,
}) => {
  const formikRef = useRef<FormikProps<AddCollaboratorFormValues>>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);
  const { setPanelActions, closePanel } = usePanel();

  useEffect(() => {
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled: !isFormValid || isLoading,
      onSubmit: () => {
        formikRef.current?.submitForm();
      },
    });
  }, [setPanelActions, isFormValid, isLoading]);

  const onSubmit = async (values: AddCollaboratorFormValues) => {
    setIsLoading(true);
    try {
      const addRes = await addCollaborator(personneMoraleId, values.email, values.isOwner);

      if (addRes.isSuccess) {
        toasterService.showToast({
          message: 'Collaborateur ajouté.',
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Green,
        });
        onSuccess();
        closePanel();
        return;
      }

      const noAccount = addRes.errorMessage?.includes("n'a pas encore de compte");

      if (noAccount) {
        if (!values.firstName || !values.lastName) {
          toasterService.showToast({
            message:
              "Cet email n'a pas de compte. Saisissez prénom et nom pour le créer.",
            displayType: DisplayTypeEnum.Toaster,
            color: GenericColors.Orange,
          });
          return;
        }

        const createRes = await createAndAddCollaborator(personneMoraleId, {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          isOwner: values.isOwner,
        });

        if (!createRes.isSuccess) {
          toasterService.showToast({
            message: createRes.errorMessage ?? 'Erreur lors de la création',
            displayType: DisplayTypeEnum.Toaster,
            color: GenericColors.Red,
          });
          return;
        }

        if (createRes.data?.temporaryPassword) {
          setCreatedPassword(createRes.data.temporaryPassword);
        }
        onSuccess();
        return;
      }

      toasterService.showToast({
        message: addRes.errorMessage ?? "Échec de l'ajout",
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
    } catch (e: any) {
      toasterService.showToast({
        message: e?.response?.data?.error?.message ?? 'Erreur inattendue',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      {isLoading && <GenericLoading loading isParentSized />}

      <Formik
        enableReinitialize
        initialValues={addCollaboratorFormConfig.initialValues}
        validationSchema={addCollaboratorFormConfig.validationSchema}
        onSubmit={onSubmit}
        innerRef={formikRef}
      >
        {({ errors, touched }) => {
          return (
            <Form className="flex flex-col gap-4 p-4">
              <ValiditySync onChange={setIsFormValid} />
              <Field
                as={InputText}
                name={AddCollaboratorFieldNames.Email}
                label="Email du collaborateur"
                placeholder="email@exemple.fr"
                required
                error={touched.email ? errors.email : undefined}
              />

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Field
                  type="checkbox"
                  name={AddCollaboratorFieldNames.IsOwner}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                Gérant du cabinet (équipe, portefeuille, habilitations, briques)
              </label>

              <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                <p className="mb-2 font-medium">
                  Si l'email n'a pas encore de compte
                </p>
                <p className="mb-3 text-xs">
                  Renseignez prénom et nom : un compte sera créé automatiquement
                  avec un mot de passe temporaire que vous devrez communiquer.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    as={InputText}
                    name={AddCollaboratorFieldNames.FirstName}
                    label="Prénom"
                    placeholder="Marie"
                  />
                  <Field
                    as={InputText}
                    name={AddCollaboratorFieldNames.LastName}
                    label="Nom"
                    placeholder="Dupont"
                  />
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>

      <ConfirmationOverlay
        isOpen={!!createdPassword}
        onClose={() => {
          setCreatedPassword(null);
          closePanel();
        }}
        onConfirm={() => {
          if (createdPassword) {
            navigator.clipboard?.writeText(createdPassword).catch(() => {});
            toasterService.showToast({
              message: 'Mot de passe copié dans le presse-papier.',
              displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Green,
            });
          }
          setCreatedPassword(null);
          closePanel();
        }}
        title="Compte créé"
        description={
          createdPassword
            ? `Mot de passe temporaire : ${createdPassword}\n\nCommuniquez-le manuellement au nouveau collaborateur. Il pourra le changer à sa première connexion.`
            : ''
        }
        confirmLabel="Copier et fermer"
        cancelLabel="Fermer"
        variant="default"
      />
    </div>
  );
};
